import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiKey } from '../../contexts/ApiKeyContext';
import { useSelectedTournament } from '../../contexts/SelectedTournamentContext';
import useTenant from '../../hooks/useTenant';
import { resetPassword, userLogin } from '../../services/UserService';
import { isValidEmail } from '../../utils/Validation';

const LoginForm: React.FC = () => {
  const { saveApiKey, clearApiKey } = useApiKey();
  const { selectedTournament } = useSelectedTournament();
  const { tId } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get('redirectTo');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const [actionsVisible, setActionsVisible] = useState(true);

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogin(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    clearApiKey();
    setError('');
    userLogin(login, password, tId)
      .then((apiKey) => {
        saveApiKey(apiKey);
        if (redirectTo) {
          navigate(redirectTo);
        } else if (selectedTournament) {
          navigate(`/tournament/${selectedTournament.id}/result`);
        } else {
          navigate('/');
        }
      })
      .catch((error) => {
        if (error.status === 400) {
          setError('Nieprawidłowy login lub hasło.');
        } else {
          setError('Wystąpił problem przy logowaniu.');
        }
      })
      .finally(() => setLoading(false));
  };

  const openResetDialog = () => {
    setResetMessage('');
    setResetEmail('');
    if (isValidEmail(login)) {
      setResetEmail(login);
    }
    setResetDialogOpen(true);
  };

  const closeResetDialog = () => {
    setResetDialogOpen(false);
  };

  const handlePasswordReset = () => {
    setResetLoading(true);
    setResetMessage('');
    resetPassword(resetEmail, tId)
      .then(() => {
        setResetMessage('Jeśli email istnieje, wysłano link do resetu hasła.');
        setActionsVisible(false);
      })
      .catch(() => {
        setResetMessage('Wystąpił błąd. Spróbuj ponownie później.');
      })
      .finally(() => {
        setResetLoading(false);
      });
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Paper elevation={3} sx={{ padding: 4, maxWidth: 400, width: '100%' }}>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Login"
            variant="outlined"
            fullWidth
            margin="normal"
            value={login}
            onChange={handleLoginChange}
            required
          />
          <TextField
            label="Hasło"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={handlePasswordChange}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && (
            <Box mt={2} color="error.main">
              {error}
            </Box>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ marginTop: 2 }}
            disabled={password.length === 0 || login.length === 0 || loading}
          >
            {loading ? 'Logowanie...' : 'Zaloguj ⚽'}
          </Button>
          <Box textAlign="right" mt={1}>
            <Button onClick={openResetDialog} size="small">
              Zapomniałeś hasła?
            </Button>
          </Box>
          <Dialog open={resetDialogOpen} fullWidth maxWidth={'sm'} onClose={closeResetDialog}>
            <DialogTitle>Resetuj hasło</DialogTitle>
            <DialogContent>
              <TextField
                label="Email"
                type="email"
                fullWidth
                margin="normal"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              {resetMessage && (
                <Typography variant="body2" color="textSecondary">
                  {resetMessage}
                </Typography>
              )}
            </DialogContent>
            {actionsVisible && (
              <DialogActions>
                <Button onClick={closeResetDialog}>Anuluj</Button>
                <Button onClick={handlePasswordReset} disabled={!resetEmail || resetLoading}>
                  {resetLoading ? 'Wysyłanie...' : 'Wyślij'}
                </Button>
              </DialogActions>
            )}
          </Dialog>
        </form>
      </Paper>
    </Box>
  );
};

export default LoginForm;
